/**
 * The tax invoice for a finished booking.
 *
 * ---------------------------------------------------------------------------
 * IT SHOWS WHAT THE CUSTOMER AGREED TO AND NOTHING ELSE
 * ---------------------------------------------------------------------------
 * ₹200 was the number on the checkout button, so ₹200 is the number on this
 * document. How that ₹200 is divided between the platform and the professional
 * is a commission record - it belongs in the ledger and it must never appear
 * here. An invoice that explains our margin to the customer invites an
 * argument about it, on a document whose entire job is to be unambiguous.
 *
 * Derived from the booking rather than stored, for now: there is no invoicing
 * service, and a hard-coded document that disagreed with the booking it names
 * would be worse than one computed from it. When the backend issues real
 * invoices, keep the shape and swap `invoiceFor` for a fetch - the screen
 * reads nothing else.
 */

import { Booking } from './bookings';
import { appConfig } from '../services/config';

export type InvoiceLine = {
  label: string;
  amount: number;
  /** Deducted rather than charged - coupons, adjustments. */
  credit?: boolean;
};

export type Invoice = {
  /** The document's own number, not the booking reference. */
  number: string;
  issuedOn: string;
  bookingRef: string;
  billedTo: string;
  address: string;
  lines: InvoiceLine[];
  /** What was charged for the services, before GST. */
  subtotal: number;
  /** The GST rate as a percentage, worked out from the two amounts. */
  taxPercent: number;
  tax: number;
  /** What the customer paid, GST included. */
  total: number;
  paidWith: string;
  /**
   * Who issued it - from Settings, never from this app. A line is the empty
   * string until ops has filled it in, and the screen leaves an empty line
   * off: a made-up GSTIN on a tax document is worse than none.
   */
  seller: { name: string; gstin: string; address: string; sac: string };
};

const sellerFromSettings = (): Invoice['seller'] => {
  const config = appConfig();
  return {
    // The brand is public and true; only the legal entity is left out until set.
    name: config.invoiceSellerName || 'Homingo',
    gstin: config.invoiceSellerGstin,
    address: config.invoiceSellerAddress,
    sac: config.invoiceSacCode,
  };
};

const toPaise = (rupees: number) => Math.round(rupees * 100);

/**
 * Whether there is an invoice to show at all.
 *
 * Issued on completion, not on payment: until the work is done there is
 * nothing to certify, and a tax document for a job that was later cancelled is
 * a document that has to be credited back.
 */
export const hasInvoice = (booking: Booking): boolean =>
  booking.stage === 'completed' && booking.status !== 'cancelled';

/**
 * @param billedTo Whoever is actually signed in - never guessed. This used to
 * be a fixed name, 'Rohan Sharma', printed on every invoice regardless of
 * whose booking it was; the caller now has to supply the real one.
 */
export const invoiceFor = (booking: Booking, billedTo: string): Invoice => {
  /*
   * ONE NUMBER IS PAID, AND THE TAX IS A PART OF IT.
   *
   * `price` is what the customer was charged for the booking - GST included,
   * whether the platform priced it with the tax inside the price or added it on
   * top - and time added while the job ran was paid for separately. This used
   * to add `taxAmount` to `price` again, so every invoice showed more than
   * the customer had paid. The total here is what was paid; the tax is the part
   * of it the server froze at completion; the subtotal is what is left.
   */
  const totalPaise = toPaise(booking.price + (booking.extensionAmount ?? 0));
  const taxPaise = Math.min(totalPaise, toPaise(booking.taxAmount ?? 0));
  const subtotalPaise = totalPaise - taxPaise;

  /*
   * One line per service, with the balance of the subtotal on the last.
   *
   * The booking carries names and one total, not per-item prices, so an even
   * split would print numbers that do not add up to anything real. Putting the
   * remainder on the final line keeps the arithmetic honest and visible: the
   * lines sum to the subtotal exactly, always.
   */
  const count = Math.max(1, booking.services.length);
  const basePaise = Math.floor(subtotalPaise / count);
  const lines: InvoiceLine[] = booking.services.map((service, i) => ({
    label: service,
    amount:
      (i === count - 1 ? subtotalPaise - basePaise * (count - 1) : basePaise) /
      100,
  }));

  return {
    number: booking.invoiceNumber ?? `INV-${booking.reference.replace(/^#/, '')}`,
    issuedOn: booking.when,
    bookingRef: booking.reference,
    billedTo,
    address: booking.address,
    lines,
    subtotal: subtotalPaise / 100,
    // From the two amounts, so the label can never disagree with them: 5% of
    // 129 is 6.45, and 6.45 on 129 reads back as 5.
    taxPercent:
      subtotalPaise > 0
        ? Math.round((taxPaise / subtotalPaise) * 1000) / 10
        : 0,
    tax: taxPaise / 100,
    total: totalPaise / 100,
    paidWith: booking.payment.method,
    seller: sellerFromSettings(),
  };
};
